import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import {
  Bug, AlertTriangle, AlertCircle, AlertOctagon, Info,
  Camera, Upload, X, Loader2, Send, CheckCircle2,
  FileText, Image as ImageIcon, Smartphone, Phone, Link as LinkIcon,
  History, Sparkles, Wrench, ChevronRight as ChevronRightIcon,
  Check, ChevronsUpDown
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SEVERIDAD_INFO = {
  bajo:    { label: "Bajo",    color: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300", icon: Info,         description: "No bloquea trabajo, sólo es molesto" },
  medio:   { label: "Medio",   color: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300",          icon: AlertCircle,  description: "Afecta tareas pero hay manera de evitarlo" },
  alto:    { label: "Alto",    color: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300",     icon: AlertTriangle,description: "Bloquea una función importante" },
  critico: { label: "Crítico", color: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300",               icon: AlertOctagon, description: "Plataforma inutilizable" },
};

const STATUS_INFO = {
  nuevo:        { label: "Nuevo",        color: "bg-blue-100 text-blue-700 border-blue-300" },
  en_revision:  { label: "En Revisión",  color: "bg-violet-100 text-violet-700 border-violet-300" },
  en_proceso:   { label: "En Proceso",   color: "bg-amber-100 text-amber-700 border-amber-300" },
  resuelto:     { label: "Resuelto",     color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  cerrado:      { label: "Cerrado",      color: "bg-slate-200 text-slate-700 border-slate-300" },
  no_aplica:    { label: "No Aplica",    color: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

export default function ReportarErrorPage() {
  const token = localStorage.getItem("token");
  const { user, sidebarMenus } = useAuth();
  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const [modulos, setModulos] = useState([]);  // fallback list from backend
  const [moduloOpen, setModuloOpen] = useState(false);

  // Helper: check if user has access to a given menu/submenu (same logic as Sidebar.jsx)
  const hasAccess = useCallback((permissionKey) => {
    const userRole = user?.role?.toLowerCase();
    if (userRole === "admin" || userRole === "administrador" || userRole === "superadmin") return true;
    if (!user?.menu_permissions) return false;
    const permission = user.menu_permissions[permissionKey];
    if (permission?.access === false) return false;
    if (permission?.access === true) return true;
    return false;
  }, [user]);

  // Build the list of modules grouped by parent menu, filtered by the user's
  // accessible permissions. Falls back to the backend MODULOS list when the
  // sidebar menus haven't loaded yet or the user has no permissions at all.
  const moduloOptions = useMemo(() => {
    const groups = [];
    const seen = new Set();
    if (Array.isArray(sidebarMenus) && sidebarMenus.length > 0) {
      for (const menu of sidebarMenus) {
        const hasDirectPath = menu.path && (!menu.submenus || menu.submenus.length === 0);
        const accessibleSubs = (menu.submenus || []).filter((sub) => {
          if (user?.menu_permissions?.[sub.id]?.access === false) return false;
          const permKey = sub.permission || sub.id;
          return hasAccess(sub.id) || hasAccess(permKey);
        });
        const items = [];
        const parentLabel = menu.label;
        // Si el menú padre tiene submenús accesibles, añadirlo también como
        // opción "área completa" para que el usuario pueda elegirla cuando
        // no sabe exactamente qué submenú es el problemático.
        if (accessibleSubs.length > 0 && parentLabel && !seen.has(parentLabel)) {
          items.push({ label: parentLabel, group: parentLabel, isParent: true });
          seen.add(parentLabel);
        }
        if (hasDirectPath && (hasAccess(menu.id) || hasAccess(menu.permission || menu.id))) {
          if (parentLabel && !seen.has(parentLabel)) {
            items.push({ label: parentLabel, group: parentLabel });
            seen.add(parentLabel);
          }
        }
        for (const sub of accessibleSubs) {
          const label = sub.label;
          if (label && !seen.has(label)) { items.push({ label, group: parentLabel }); seen.add(label); }
        }
        if (items.length > 0) groups.push({ group: parentLabel, items });
      }
    }
    // If we couldn't derive any group (sidebar still loading, or user role
    // resolves to admin who sees everything but we want the same UX) — fall
    // back to the backend exhaustive list.
    if (groups.length === 0 && modulos.length > 0) {
      groups.push({ group: "Módulos del sistema", items: modulos.map((m) => ({ label: m, group: "Módulos del sistema" })) });
    }
    // Always offer "Otro / No estoy seguro" + "Login / Acceso al sistema" at the end
    const extras = [
      { label: "Login / Acceso al sistema", group: "Otros" },
      { label: "Otro / No estoy seguro", group: "Otros" },
    ];
    const extrasFiltered = extras.filter((e) => !seen.has(e.label));
    if (extrasFiltered.length > 0) groups.push({ group: "Otros", items: extrasFiltered });
    return groups;
  }, [sidebarMenus, user, hasAccess, modulos]);

  const totalModulosVisibles = useMemo(
    () => moduloOptions.reduce((acc, g) => acc + g.items.length, 0),
    [moduloOptions]
  );

  // Auto-detección del módulo a partir de la ruta de origen (referrer interno).
  // Helper que se usa más abajo en un useEffect (después de declarar `form`).
  const findModuloFromPath = useCallback((path) => {
    if (!path || !Array.isArray(sidebarMenus)) return null;
    // Normaliza: quita query params, hash y trailing slashes
    const norm = (s) => (s || "").split("?")[0].split("#")[0].replace(/\/+$/, "");
    const target = norm(path);
    if (!target) return null;
    let best = null;  // { label, score } — score = length del match (mayor = más específico)
    for (const menu of sidebarMenus) {
      const checkItem = (lbl, p) => {
        if (!lbl || !p) return;
        const np = norm(p);
        if (!np) return;
        if (target === np || target.startsWith(np + "/")) {
          if (!best || np.length > best.score) best = { label: lbl, score: np.length };
        }
      };
      checkItem(menu.label, menu.path);
      for (const sub of (menu.submenus || [])) {
        checkItem(sub.label, sub.path);
        for (const nested of (sub.submenus || [])) {
          checkItem(nested.label, nested.path);
        }
      }
    }
    return best?.label || null;
  }, [sidebarMenus]);
  const [form, setForm] = useState({
    asunto: "",
    descripcion: "",
    modulo: "",
    severidad: "medio",
    pasos_para_reproducir: "",
    url_problema: typeof window !== "undefined" ? window.location.href : "",
    navegador: typeof navigator !== "undefined" ? navigator.userAgent : "",
    dispositivo: typeof navigator !== "undefined" ? `${navigator.platform || ""}` : "",
    contacto_telefono: "",
    screenshot_urls: [],
  });
  const [screenshots, setScreenshots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [misReportes, setMisReportes] = useState([]);
  const [loadingMis, setLoadingMis] = useState(false);
  const [roadmap, setRoadmap] = useState([]);
  const [roadmapOpen, setRoadmapOpen] = useState(null);
  const [loadingRoadmap, setLoadingRoadmap] = useState(false);

  // Auto-detección del módulo a partir del referrer interno
  // (declarado aquí porque depende de `form.modulo` para no re-aplicarse).
  useEffect(() => {
    if (form.modulo) return;
    if (!sidebarMenus || sidebarMenus.length === 0) return;
    let referrer = "";
    try { referrer = sessionStorage.getItem("report_referrer_path") || ""; } catch (_) { /* noop */ }
    if (!referrer) return;
    const matchedLabel = findModuloFromPath(referrer);
    if (matchedLabel) {
      setForm((prev) => ({ ...prev, modulo: matchedLabel }));
      toast.info(`Detectamos que venías de "${matchedLabel}". Si no es el módulo correcto, podés cambiarlo.`, { duration: 5000 });
      try { sessionStorage.removeItem("report_referrer_path"); } catch (_) { /* noop */ }
    }
  }, [sidebarMenus, findModuloFromPath, form.modulo]);

  // Carga inicial: opciones, reportes previos del usuario y roadmap público
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reportes-errores/_options/all`, getAuthHeaders());
        setModulos(r.data.modulos || []);
      } catch (e) { /* noop */ }
    })();
    fetchMisReportes();
    fetchRoadmap();
  }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMisReportes = async () => {
    setLoadingMis(true);
    try {
      const r = await axios.get(`${API}/reportes-errores?limit=20`, getAuthHeaders());
      setMisReportes(r.data.items || []);
    } catch (e) { /* noop */ }
    setLoadingMis(false);
  };

  const fetchRoadmap = async () => {
    setLoadingRoadmap(true);
    try {
      const r = await axios.get(`${API}/reportes-errores/roadmap/recientes?limit=12`, getAuthHeaders());
      setRoadmap(r.data.items || []);
    } catch (e) { /* noop */ }
    setLoadingRoadmap(false);
  };

  const handleScreenshotUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newShots = [...screenshots];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/reportes-errores/upload-screenshot`, fd, {
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
        });
        newShots.push({ url: r.data.url, filename: r.data.filename, name: file.name });
      } catch (err) {
        toast.error(`No se pudo subir ${file.name}`);
      }
    }
    setScreenshots(newShots);
    setForm({ ...form, screenshot_urls: newShots.map(s => s.url) });
    setUploading(false);
    e.target.value = "";
  };

  const removeScreenshot = (idx) => {
    const ns = screenshots.filter((_, i) => i !== idx);
    setScreenshots(ns);
    setForm({ ...form, screenshot_urls: ns.map(s => s.url) });
  };

  const handleSubmit = async () => {
    if (!form.asunto.trim() || form.asunto.trim().length < 5) {
      toast.error("El asunto debe tener al menos 5 caracteres");
      return;
    }
    if (!form.descripcion.trim() || form.descripcion.trim().length < 10) {
      toast.error("La descripción debe tener al menos 10 caracteres");
      return;
    }
    if (!form.modulo) {
      toast.error("Selecciona el módulo donde ocurrió el problema");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reportes-errores`, form, getAuthHeaders());
      toast.success("Reporte enviado. ¡Gracias por ayudarnos a mejorar! 🙌");
      setSubmitted(true);
      fetchMisReportes();
      fetchRoadmap();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el reporte");
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setForm({
      asunto: "", descripcion: "", modulo: "", severidad: "medio",
      pasos_para_reproducir: "",
      url_problema: typeof window !== "undefined" ? window.location.href : "",
      navegador: typeof navigator !== "undefined" ? navigator.userAgent : "",
      dispositivo: typeof navigator !== "undefined" ? `${navigator.platform || ""}` : "",
      contacto_telefono: "",
      screenshot_urls: [],
    });
    setScreenshots([]);
    setSubmitted(false);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-rose-50/40 via-white to-amber-50/40 dark:from-rose-950/20 dark:via-slate-900 dark:to-amber-950/10">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="reportar-error-page">

        {/* Hero header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 text-white px-6 py-8 mb-8 shadow-xl shadow-rose-200/40 dark:shadow-rose-900/30">
          <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -right-16 bottom-0 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
          <div className="relative flex items-start gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 ring-1 ring-white/30">
              <Bug className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-white/90 text-rose-600 border-0 font-semibold tracking-wide text-[10px] uppercase">
                  <Sparkles className="w-2.5 h-2.5 mr-1" /> Soporte
                </Badge>
              </div>
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                Reportar un problema
              </h1>
              <p className="text-white/90 mt-1.5 text-sm max-w-2xl">
                ¿Encontraste un error? ¿Algo no funciona como esperabas? Cuéntanos los detalles
                y nuestro equipo lo va a revisar lo antes posible. Cuanta más información nos
                des, más rápido podremos resolverlo.
              </p>
            </div>
          </div>
        </div>

        {/* Roadmap público: bugs resueltos recientemente */}
        {(roadmap.length > 0 || loadingRoadmap) && (
          <div className="mb-7" data-testid="roadmap-resueltos-section">
            <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h2 className="text-base font-bold font-['Manrope'] tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                  <Wrench className="w-4 h-4 text-emerald-600" />
                  Lo que arreglamos últimamente
                </h2>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Antes de reportar, revisá si tu problema ya fue resuelto · {roadmap.length} de los últimos 60 días
                </p>
              </div>
              {loadingRoadmap && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
            <div className="relative">
              <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
                {roadmap.map((r) => {
                  const sevColor = SEVERIDAD_INFO[r.severidad]?.color || SEVERIDAD_INFO.medio.color;
                  const isOpen = roadmapOpen === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setRoadmapOpen(isOpen ? null : r.id)}
                      className={`snap-start text-left rounded-xl border bg-white dark:bg-slate-900 transition-all flex-shrink-0 px-3 py-2.5 group ${
                        isOpen
                          ? "w-[420px] border-emerald-300 dark:border-emerald-700 shadow-lg shadow-emerald-100/50 dark:shadow-emerald-950/30"
                          : "w-[260px] border-slate-200 dark:border-slate-700 hover:border-emerald-300 hover:shadow-sm"
                      }`}
                      data-testid={`roadmap-card-${r.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="rounded-lg p-1.5 bg-emerald-100 dark:bg-emerald-900/40 flex-shrink-0">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${sevColor}`}>
                              {SEVERIDAD_INFO[r.severidad]?.label || r.severidad}
                            </Badge>
                            <span className="text-[9px] text-slate-400">
                              {new Date(r.created_at).toLocaleDateString("es-DO", { month: "short", day: "numeric" })}
                            </span>
                          </div>
                          <p className={`text-xs font-semibold text-slate-700 dark:text-slate-200 ${isOpen ? "" : "truncate"}`}>
                            {r.asunto}
                          </p>
                          {r.modulo && (
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{r.modulo}</p>
                          )}
                          {isOpen && r.resolution_notes && (
                            <div className="mt-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40 p-2">
                              <p className="text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold mb-0.5">
                                Nota del equipo
                              </p>
                              <p className="text-[11px] text-emerald-900 dark:text-emerald-100 whitespace-pre-wrap leading-snug">
                                {r.resolution_notes}
                              </p>
                            </div>
                          )}
                          {isOpen && r.resolved_by && (
                            <p className="text-[9px] text-slate-400 mt-1.5">
                              Resuelto por <strong>{r.resolved_by}</strong>
                            </p>
                          )}
                        </div>
                        {!isOpen && <ChevronRightIcon className="w-3 h-3 text-slate-300 group-hover:text-emerald-500 flex-shrink-0 mt-1" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {submitted ? (
          // Pantalla de éxito
          <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-900 dark:text-emerald-100 mb-2">
                ¡Reporte enviado!
              </h2>
              <p className="text-emerald-800 dark:text-emerald-200 mb-6 max-w-md mx-auto">
                Gracias por tomarte el tiempo de ayudarnos a mejorar la plataforma.
                El equipo revisará tu reporte y te contactará si necesita más información.
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={resetForm} className="bg-emerald-600 hover:bg-emerald-500" data-testid="reportar-otro-btn">
                  <Bug className="w-4 h-4 mr-2" /> Reportar otro problema
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formulario */}
            <div className="lg:col-span-2 space-y-5">
              {/* Severidad — selector visual */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="pt-5 pb-5">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">
                    Severidad del problema *
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {Object.entries(SEVERIDAD_INFO).map(([k, info]) => {
                      const Icon = info.icon;
                      const isSelected = form.severidad === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setForm({ ...form, severidad: k })}
                          className={`rounded-xl border-2 px-3 py-3 text-left transition-all ${isSelected ? `${info.color} ring-2 ring-offset-2 ring-rose-400 dark:ring-offset-slate-900` : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"}`}
                          data-testid={`sev-${k}-btn`}
                        >
                          <Icon className="w-5 h-5 mb-1.5" />
                          <p className="font-semibold text-sm">{info.label}</p>
                          <p className="text-[11px] opacity-80 mt-0.5 leading-tight">{info.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Asunto + Módulo */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <div>
                    <Label htmlFor="re-asunto" className="text-sm font-semibold mb-1.5 block flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-rose-500" />
                      Resumen breve *
                    </Label>
                    <Input
                      id="re-asunto"
                      value={form.asunto}
                      onChange={(e) => setForm({ ...form, asunto: e.target.value })}
                      placeholder='Ej: "El botón de exportar Excel no descarga el archivo"'
                      maxLength={200}
                      className="border-slate-300 focus:border-rose-400 focus:ring-rose-300"
                      data-testid="asunto-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{form.asunto.length}/200</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-1.5 block">
                      ¿En qué parte del sistema pasó? *
                      <span className="text-[10px] font-normal text-slate-400 ml-1">
                        ({totalModulosVisibles} módulos disponibles)
                      </span>
                    </Label>
                    <Popover open={moduloOpen} onOpenChange={setModuloOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={moduloOpen}
                          className={`w-full justify-between border-slate-300 focus:ring-rose-300 font-normal ${form.modulo ? "" : "text-slate-400"}`}
                          data-testid="modulo-select"
                        >
                          <span className="truncate">
                            {form.modulo || "Buscá o seleccioná el módulo afectado"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command shouldFilter={true} filter={(value, search) => {
                          if (!search) return 1;
                          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                        }}>
                          <CommandInput
                            placeholder="Escribí para buscar… (ej: cotizaciones, tickets)"
                            className="h-9"
                            data-testid="modulo-search-input"
                          />
                          <CommandList className="max-h-[280px]">
                            <CommandEmpty>No encontramos ese módulo. Probá con otra palabra o elegí "Otro / No estoy seguro".</CommandEmpty>
                            {moduloOptions.map((grp) => (
                              <CommandGroup key={grp.group} heading={grp.group}>
                                {grp.items.map((it) => (
                                  <CommandItem
                                    key={`${grp.group}-${it.label}`}
                                    value={`${it.label} ${grp.group}`}
                                    onSelect={() => {
                                      setForm({ ...form, modulo: it.label });
                                      setModuloOpen(false);
                                    }}
                                    data-testid={`modulo-option-${it.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                    className="text-sm"
                                  >
                                    <Check
                                      className={`mr-2 h-3.5 w-3.5 ${form.modulo === it.label ? "opacity-100 text-rose-500" : "opacity-0"}`}
                                    />
                                    <span className={it.isParent ? "font-semibold" : ""}>{it.label}</span>
                                    {it.isParent && (
                                      <span className="ml-2 text-[9px] text-slate-400 font-normal">área completa</span>
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </CardContent>
              </Card>

              {/* Descripción + Pasos */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <div>
                    <Label htmlFor="re-desc" className="text-sm font-semibold mb-1.5 block">
                      ¿Qué pasó? *
                    </Label>
                    <Textarea
                      id="re-desc"
                      value={form.descripcion}
                      onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                      placeholder="Describe el problema con tus palabras. ¿Qué esperabas que pasara y qué pasó realmente?"
                      rows={5}
                      maxLength={4000}
                      className="border-slate-300 focus:border-rose-400 focus:ring-rose-300 resize-y"
                      data-testid="descripcion-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{form.descripcion.length}/4000</p>
                  </div>
                  <div>
                    <Label htmlFor="re-pasos" className="text-sm font-semibold mb-1.5 block">
                      Pasos para reproducir el problema <span className="text-slate-400 font-normal">(opcional, pero ayuda mucho)</span>
                    </Label>
                    <Textarea
                      id="re-pasos"
                      value={form.pasos_para_reproducir}
                      onChange={(e) => setForm({ ...form, pasos_para_reproducir: e.target.value })}
                      placeholder={`1. Abrí el módulo de Cotizaciones\n2. Hice click en "Exportar Excel"\n3. No se descargó nada y salió un mensaje de error`}
                      rows={4}
                      maxLength={2000}
                      className="border-slate-300 focus:border-rose-400 focus:ring-rose-300 resize-y font-mono text-xs"
                      data-testid="pasos-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Screenshots */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="pt-5 pb-5">
                  <Label className="text-sm font-semibold mb-3 block flex items-center gap-1.5">
                    <Camera className="w-4 h-4 text-rose-500" />
                    Capturas de pantalla <span className="text-slate-400 font-normal">(hasta 8MB c/u)</span>
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {screenshots.map((s, idx) => (
                      <div key={idx} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt={s.name} className="w-full h-24 object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(idx)}
                          className="absolute -top-2 -right-2 bg-rose-600 hover:bg-rose-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <label className="flex flex-col items-center justify-center h-24 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-rose-400 dark:hover:border-rose-500 cursor-pointer transition-colors bg-slate-50 dark:bg-slate-800/50">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-rose-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-[11px] text-slate-500">Subir imagen</span>
                        </>
                      )}
                      <input type="file" accept="image/*" multiple onChange={handleScreenshotUpload} disabled={uploading} className="hidden" data-testid="screenshot-input" />
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Contacto + URL */}
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold mb-1.5 block flex items-center gap-1.5">
                        <LinkIcon className="w-4 h-4 text-rose-500" />
                        URL donde pasó
                      </Label>
                      <Input
                        value={form.url_problema}
                        onChange={(e) => setForm({ ...form, url_problema: e.target.value })}
                        placeholder="https://..."
                        className="border-slate-300 focus:border-rose-400 focus:ring-rose-300 text-xs"
                        data-testid="url-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold mb-1.5 block flex items-center gap-1.5">
                        <Phone className="w-4 h-4 text-rose-500" />
                        Teléfono de contacto (opcional)
                      </Label>
                      <Input
                        value={form.contacto_telefono}
                        onChange={(e) => setForm({ ...form, contacto_telefono: e.target.value })}
                        placeholder="Por si necesitamos llamarte"
                        className="border-slate-300 focus:border-rose-400 focus:ring-rose-300 text-xs"
                        data-testid="telefono-input"
                      />
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3">
                    <div className="flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                      <Smartphone className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">Información técnica capturada automáticamente:</p>
                        <p className="font-mono truncate">{form.navegador?.substring(0, 100)}{(form.navegador?.length || 0) > 100 ? "..." : ""}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="lg"
                  className="bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-700 hover:to-orange-600 text-white shadow-lg shadow-rose-200/50 dark:shadow-rose-900/30 px-8"
                  data-testid="submit-reporte-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Enviar reporte</>
                  )}
                </Button>
              </div>
            </div>

            {/* Sidebar: mis reportes previos */}
            <div className="space-y-4">
              <Card className="border-slate-200 dark:border-slate-800 sticky top-4">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-1.5">
                    <History className="w-4 h-4 text-rose-500" />
                    Mis reportes anteriores
                  </h3>
                  {loadingMis ? (
                    <div className="flex items-center justify-center py-8 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  ) : misReportes.length === 0 ? (
                    <div className="text-center py-8 text-xs text-slate-400">
                      <ImageIcon className="w-8 h-8 mx-auto opacity-30 mb-2" />
                      <p>No has reportado problemas aún.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                      {misReportes.map((r) => {
                        const stInfo = STATUS_INFO[r.status] || STATUS_INFO.nuevo;
                        const sevInfo = SEVERIDAD_INFO[r.severidad] || SEVERIDAD_INFO.medio;
                        return (
                          <div key={r.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 hover:border-rose-300 dark:hover:border-rose-700 transition-colors">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${sevInfo.color}`}>{sevInfo.label}</Badge>
                              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${stInfo.color}`}>{stInfo.label}</Badge>
                            </div>
                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{r.asunto}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{r.modulo} · {new Date(r.created_at).toLocaleDateString("es-DO")}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
